import React, { useRef, useState } from 'react';

import api, { ApiError } from '../../utils/api';
import { toast } from '../../utils/swal';

import './ContactForm.css';

const INITIAL = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  _trap: '', // honeypot
};

export default function ContactForm() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const formRef = useRef(null);

  const setField = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => {
      if (!e[name]) return e;
      const next = { ...e };
      delete next[name];
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'Required';
    if (!form.lastName.trim()) errs.lastName = 'Required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email || '')) {
      errs.email = 'That email looks off.';
    }
    if (!form.phone.trim()) {
      errs.phone = 'Required';
    } else if (!/^[\d\s().+-]{7,30}$/.test(form.phone)) {
      errs.phone = 'Invalid phone number';
    }
    if (!form.subject.trim()) errs.subject = 'Required';
    if (!form.message.trim()) errs.message = 'Required';
    else if (form.message.trim().length < 10) errs.message = 'A bit more, please.';
    return errs;
  };

  const scrollToFirstError = (errs) => {
    const order = ['firstName', 'lastName', 'email', 'phone', 'subject', 'message'];
    const firstKey = order.find((k) => errs[k]);
    if (!firstKey || !formRef.current) return;
    const el = formRef.current.querySelector(`[name="${firstKey}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => el.focus({ preventScroll: true }), 350);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const clientErrs = validate();
    if (Object.keys(clientErrs).length) {
      setErrors(clientErrs);
      scrollToFirstError(clientErrs);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        website: form._trap,
      };
      await api.post('/api/contact', payload);
      setSubmitted(true);
      setForm(INITIAL);
      toast('Message sent. We\u2019ll be in touch.', 'success');
    } catch (err) {
      if (err instanceof ApiError && err.errors) {
        setErrors(err.errors);
        scrollToFirstError(err.errors);
      } else {
        const msg = (err && err.message) || 'Could not send your message. Please try again.';
        setErrors((e) => ({ ...e, _form: msg }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact">
      <div className="contact__inner">
        <aside className="contact__head">
          <div className="contact__kicker mono">
            <span className="contact__num">02</span>
            <span>Questions or anything else</span>
          </div>

          <h2 className="contact__heading display">
            Say <em>hello</em>.
          </h2>
          <p className="contact__intro">
            Have a question, a special request, or just want to chat about a
            look you've been thinking about? Drop a note — we read every one.
          </p>

          <div className="contact__details">
            <div className="contact__detail">
              <div className="mono contact__detail-label">Hours</div>
              <div className="contact__detail-value display">Mon–Fri · 9–4</div>
            </div>
            <div className="contact__detail">
              <div className="mono contact__detail-label"></div>
              <div className="contact__detail-value display"></div>
            </div>
          </div>
        </aside>

        <form
          className="contact__form"
          onSubmit={handleSubmit}
          ref={formRef}
          noValidate
        >
          <div className="contact__honey" aria-hidden="true">
            <label>
              Website
              <input
                type="text"
                name="_trap"
                tabIndex={-1}
                autoComplete="nope"
                value={form._trap}
                onChange={handleChange}
              />
            </label>
          </div>

          <div className="contact__grid">
            <CField label="First name" required error={errors.firstName}>
              <input
                type="text"
                name="firstName"
                autoComplete="given-name"
                value={form.firstName}
                onChange={handleChange}
                maxLength={60}
              />
            </CField>

            <CField label="Last name" required error={errors.lastName}>
              <input
                type="text"
                name="lastName"
                autoComplete="family-name"
                value={form.lastName}
                onChange={handleChange}
                maxLength={60}
              />
            </CField>

            <CField label="Email" required error={errors.email}>
              <input
                type="email"
                name="email"
                autoComplete="email"
                value={form.email}
                onChange={handleChange}
                maxLength={120}
              />
            </CField>

            <CField label="Phone" required error={errors.phone}>
              <input
                type="tel"
                name="phone"
                autoComplete="tel"
                inputMode="tel"
                value={form.phone}
                onChange={handleChange}
                maxLength={30}
              />
            </CField>

            <CField label="Subject" required error={errors.subject} full>
              <input
                type="text"
                name="subject"
                placeholder="What's this about?"
                value={form.subject}
                onChange={handleChange}
                maxLength={200}
              />
            </CField>

            <CField label="Message" required error={errors.message} full>
              <textarea
                name="message"
                rows={5}
                placeholder="Tell us what you have in mind…"
                value={form.message}
                onChange={handleChange}
                maxLength={5000}
              />
            </CField>
          </div>

          {errors._form && (
            <div className="contact__form-error" role="alert">
              {errors._form}
            </div>
          )}

          {submitted && (
            <div className="contact__success" role="status">
              Got it. Someone from the team will reply within Few business day.
            </div>
          )}

          <div className="contact__actions">
            <button
              type="submit"
              className="contact__submit"
              disabled={submitting}
            >
              <span>{submitting ? 'Sending…' : 'Send message'}</span>
              <span className="contact__arrow" aria-hidden="true">→</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CField({ label, required, error, children, full }) {
  return (
    <div className={`contact__field ${full ? 'contact__field--full' : ''} ${error ? 'is-invalid' : ''}`}>
      <label className="contact__label mono">
        {label}
        {required && <span className="contact__req">*</span>}
      </label>
      {children}
      {error && <span className="contact__err">{error}</span>}
    </div>
  );
}